import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AdminService } from "../../admin.service";
import { Router } from '@angular/router';
import { UsersService } from 'src/app/users/users.service';
import { ColumnsConfig, TableConfig, TableData} from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { TranslateService } from '@ngx-translate/core';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { CommonDataService } from 'src/app/shared/services/common-data.service';
import { CommonService } from 'src/app/shared/services/common.service';
import * as lo from 'lodash-es';
import { DeviceManagementService, DevicesFamily } from 'src/app/device/device-management.service';
import { SitesService } from 'src/app/sites/sites.service';
import * as moment from 'moment';
import { iotemeterWarningLabel } from '../iotemeter-warning-label';

@Component({
		selector: 'app-connectivity-verification-statistics',
		templateUrl: './connectivity-verification-statistics.component.html',
		styleUrls: ['./connectivity-verification-statistics.component.css']
})

export class ConnectivityVerificationStatisticsComponent implements OnInit, OnDestroy {
	@ViewChild("devicesInfoModel") devicesInfoModel;
	@ViewChild("sitesInfoModel") sitesInfoModel;
	@ViewChild("waningDevicesInfoModel") waningDevicesInfoModel;

	// connectivity_verification_statistics table data
	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: false,
		draggable: true,
		fileName: this.translate.instant('connectivity_verification.connectivity_verification_header'),
		csvFileHeaderInfo: [this.translate.instant('g.export_time'), moment().format('YYYY-MM-DD HH:mm:ss [UTC]Z')]
	};

	columnConfig: ColumnsConfig[] = [
		{ key: 'device_family', name: this.translate.instant('connectivity_verification.device_family'), type: 'string'},
		{ key: 'month', name: this.translate.instant('connectivity_verification.month'), type: 'string'},
		{ key: 'total_check', name: this.translate.instant('connectivity_verification.total_check'), type: 'number'},
		{ key: 'total_number_unique_devices', name: this.translate.instant('connectivity_verification.total_number_unique_devices'), type: 'number_link'},
		{ key: 'total_number_unique_sites', name: this.translate.instant('connectivity_verification.total_number_unique_sites'), type: 'number_link'},
	];

	// Hardware Errors Statistics table data
	hardwareErrorsTableData: TableData[] = [];
	hardwareErrorsTableConfig: TableConfig = {
		hasExport: true,
		hasPagination: false,
		draggable: true,
		fileName: this.translate.instant('connectivity_verification.connectivity_verification_header'),
		hasRowSpan: true,
		separatedCells: true,
		csvFileHeaderInfo: [this.translate.instant('g.export_time'), moment().format('YYYY-MM-DD HH:mm:ss [UTC]Z')]
	};

	hardwareErrorsColumnConfig: ColumnsConfig[] = [
		{
			key: 'device_family', name: this.translate.instant('connectivity_verification.device_family'),
			type: 'string', hasSort: false, hasFilter: false
		},
		{ key: 'hardware_version', name: this.translate.instant('g.hardware_version'), type: 'string', hasSort: false, hasFilter: false },
		{ key: 'warning_name', name: this.translate.instant('warnings.warning_name'), type: 'string', hasSort: false, hasFilter: false },
		{ key: 'number_of_occurrences', name: this.translate.instant('connectivity_verification.number_of_occurrences'), type: 'number_link', hasSort: false, hasFilter: false },
	];

	sitesInfo: any;
	devicesInfo: any;
	devicesData: any[] = [];

	waningDevicesData: any[] = [];

	sitesData: any[] = [];
	modalTitle: string = '';

	activeTable = 'connectivity_verification_statistics';


	constructor(
		private adminService: AdminService,
		private usersService: UsersService,
		private router: Router,
		private translate: TranslateService,
		private commonDataService: CommonDataService,
		private commonService: CommonService,
		private deviceManagementService: DeviceManagementService,
		private sitesService: SitesService
	) {}

	ngOnInit() {
			if (!this.usersService.hasAccessPermission(null, 'noc'))
					this.router.navigate(['/unauthorized']);

			this.getConnectivityVerificationStatistics();
	}

	getConnectivityVerificationStatistics() {
		this.adminService.getConnectivityVerificationStatistics().subscribe((response: any) => {
			if (!Object.keys(response).length)
					return;

			const monthList = this.commonDataService.MONTHS_LIST;
			const tableData = [];

			for(const record of response.statisticsLogs) {
				const monthObject = monthList.find(month => month.label == record.month);
				const monthNumber = monthObject ? monthObject.value : 0;

				tableData.push({
					id: record.id,
					device_family: {value: this.familyLabel(record.device_family)},
					month: {
						value: `${record.month} ${record.year}`,
						sortValue: `${record.year}-${`${monthNumber}`.padStart(2, '0')}`
					},
					total_check: {value: record.total_checks},
					total_number_unique_devices: {
						value: record.mac_addresses.length,
						action: ()=> {
							this.showDevicesInfoModal({ record, macAddress: record.mac_addresses, deviceFamily: record.device_family });
						}
					},
					total_number_unique_sites: {
						value: record.sites_ids.length,
						action: record.device_family == DevicesFamily.IOTEMETER ? null : ()=> {
							this.showSitesInfoModal({ record: record, site_ids: record.sites_ids });
						}
					},
				})
			}

			this.tableData = tableData;
		});
	}

	getHardwareErrorsStatistics() {
		this.adminService.getHardwareErrorsStatistics().subscribe((response: any) => {
			const hardwareErrorsTableData: TableData[] = [];
			const {versionWarningsDevices} = response;

			for (const deviceFamily of Object.keys(versionWarningsDevices || {})) {
				const versions = lo.mapValues(versionWarningsDevices[deviceFamily] || {},
					(warnings) => this.groupWarningsByLabel(warnings || {}, deviceFamily));
				const sortedVersions = this.customVersionSort(Object.keys(versions));

				const familyRowCount = sortedVersions
					.reduce((total, version) => total + Object.keys(versions[version] || {}).length, 0);
				let familyCellPending = true;

				for (const version of sortedVersions) {
					const warnings = versions[version] || {};
					const warningsCount = Object.keys(warnings).length;

					let rowData: TableData = {};
					if (familyCellPending) {
						rowData['device_family'] = { value: this.familyLabel(deviceFamily), rowspan: familyRowCount };
						familyCellPending = false;
					}
					rowData['hardware_version'] = { value: version, rowspan: warningsCount };

					const warningsSortedByDeviceCount = Object.keys(warnings).sort((x, y) => {
						return (warnings[y] || []).length - (warnings[x] || []).length;
					});

					for (const warning of warningsSortedByDeviceCount) {
						const devices = warnings[warning];

						rowData['warning_name'] = { value: this.warningLabel(warning, deviceFamily) };
						rowData['number_of_occurrences'] = {
							value: devices.length,
							action: () => {
								this.showWarningDevicesModal(devices, version, warning, deviceFamily);
							}
						};

						hardwareErrorsTableData.push(rowData);
						rowData = {};
					}
				}
			}

			this.hardwareErrorsTableData = hardwareErrorsTableData;
		});
	}

	familyLabel(deviceFamily: string) {
		return this.translate.instant(deviceFamily == DevicesFamily.IOTEMETER
			? 'connectivity_verification.iotemeter_family'
			: 'connectivity_verification.iotah_family');
	}

	warningLabel(warning: string, deviceFamily: string) {
		return deviceFamily == DevicesFamily.IOTEMETER
			? iotemeterWarningLabel(this.translate, warning)
			: this.translate.instant(`warnings.${warning}`);
	}

	/**
	 * ST-2683: one IoTEmeter hardware version can hold the same warning twice - under the text older
	 * checks stored and under the code newer checks store - so both are merged under the name shown.
	 */
	groupWarningsByLabel(warnings: {[warning: string]: string[]}, deviceFamily: string) {
		if (deviceFamily != DevicesFamily.IOTEMETER)
			return warnings;

		const grouped: {[label: string]: string[]} = {};
		for (const [warning, devices] of Object.entries(warnings)) {
			const label = this.warningLabel(warning, deviceFamily);
			grouped[label] = lo.uniq([...(grouped[label] || []), ...(devices || [])]);
		}

		return grouped;
	}

	customVersionSort(versions) {
		return versions.sort((a, b) => {
			if (b.length !== a.length) {
				return b.length - a.length; // shorter comes first
			}
			return b.localeCompare(a); // alphabetical if same length
		});
	}

	showDevicesInfoModal(data) {
		const title = `${this.translate.instant('connectivity_verification.total_number_unique_devices')} - ${data.record.month} ${data.record.year}`;

		this.openDevicesModal(data.macAddress, title, this.devicesInfoModel, {
			devicesData: data.macAddress,
			'devicesInfoModel.data': data.record.id
		}, data.deviceFamily);
		this.devicesInfoModel.data = data.record.id;
	}

	openDevicesModal(deviceList: string[], title: string, modalRef: any, extraData: any = {}, deviceFamily?: string) {
		this.deviceManagementService
			.searchDevices(deviceList, 'mac', false, false, 'normal-search', true, deviceFamily as DevicesFamily)
			.subscribe((res: any) => {
			this.devicesInfo = lo.keyBy(res.devices || [], 'mac_address');
			this.sitesInfo = res.sites;

			Object.assign(this, extraData);
			this.modalTitle = title;
			modalRef.show();
		});
	}

	showSitesInfoModal(data) {
		this.sitesService.getSites([data.site_ids], {get_enterprise_list: false, formattedDate: true, get_devices: false}).subscribe((res: any) => {
			this.sitesInfo = lo.keyBy(res.site || [], 'id');
			this.sitesData = Object.keys(this.sitesInfo);
			this.modalTitle = `${this.translate.instant('connectivity_verification.total_number_unique_sites')} - ${data.record.month} ${data.record.year}`;
			this.sitesInfoModel.data = data.record.id;
			this.sitesInfoModel.show();
		});
	}

	showWarningDevicesModal(devices, hardwareVersion, warningName, deviceFamily?: string) {
		const translatedWarning = this.warningLabel(warningName, deviceFamily);
		const title = `${this.translate.instant('g.hardware_version')} - ${hardwareVersion}, ${this.translate.instant('warnings.warning_name')} - ${translatedWarning}`;
		this.openDevicesModal(devices, title, this.waningDevicesInfoModel, {
			waningDevicesData: devices
		}, deviceFamily);
	}

	exportUniqueDevicesFile() {
		const headersInfo = this.commonService.getCsvFileHeadersInfo({});
		const csvRows = [...headersInfo];

		const fileData = this.tableData.filter(row => row.id == this.devicesInfoModel.data);
		const recordDate = fileData[0].month.value;
		const columnNames = [
			this.translate.instant('g.mac_address'),
			this.translate.instant('g.serial_number')
		];

		csvRows.push(columnNames);

		(this.devicesData || []).forEach(row => {
			csvRows.push([
				row,
				this.devicesInfo[row].serial_number
			]);
		});

		new AngularCsv(csvRows, `${this.translate.instant('connectivity_verification.connectivity_verification_header')} - ${recordDate} Devices`);
	}

	exportUniqueSitesInfo() {
		const headersInfo = this.commonService.getCsvFileHeadersInfo({});
		const csvRows = [...headersInfo];
		const fileData = this.tableData.filter(row => row.id == this.sitesInfoModel.data);
		const recordDate = fileData[0].month.value;

		const columnNames = [
			this.translate.instant('g.customer_name'),
			this.translate.instant('g.site_name'),
		];
		csvRows.push(columnNames);

		(this.sitesData || []).forEach(row => {
			if (this.sitesInfo[row].name == 'site_deleted')
				csvRows.push(['', this.translate.instant(`connectivity_verification.site_deleted`)]);
			else
				csvRows.push([this.sitesInfo[row].customer_name, this.sitesInfo[row].name]);
		});

		new AngularCsv(csvRows, `${this.translate.instant('connectivity_verification.connectivity_verification_header')} - ${recordDate} Sites`);
	}

	exportWaningDevicesFile() {
		const headersInfo = this.commonService.getCsvFileHeadersInfo({});
		const csvRows = [...headersInfo];

		const columnNames = [ this.translate.instant('g.mac_address'), this.translate.instant('g.serial_number') ];
		csvRows.push(columnNames);

		(this.waningDevicesData || []).forEach(row => {
			csvRows.push([
				row,
				this.devicesInfo[row].serial_number
			]);
		});

		new AngularCsv(csvRows, this.modalTitle);
	}

	setActiveTable(table: string) {
		this.activeTable = table;

		if (this.hardwareErrorsTableData.length === 0 && table === 'hardware_errors_statistics')
			this.getHardwareErrorsStatistics();
	}

	ngOnDestroy() {
		this.sitesInfoModel.hide();
		this.devicesInfoModel.hide();
		this.waningDevicesInfoModel.hide();
	}
}
